import { autorun } from 'mobx';
import { ANALYTICS_EVENTS, INPUT_AUTOCOMPLETE, INPUT_TYPE, schemas } from '@nandosaus/constants';
import { useRootStore } from '@nandosaus/state-management';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import React, { useCallback, useEffect, useState } from 'react';

import { Alert } from '../alert';
import { analytics } from '../../analytics';
import { Box } from '../grid';
import { Form } from '../form';
import { FormikTextInput } from '../input';
import { MFAForm } from '../mfa-form';
import { ResetCodeForm } from './reset-code';

dayjs.extend(duration);

const ForgotPasswordForm = () => {
  const [error, setError] = useState();
  const [mfa, setMfa] = useState({});
  const [nextEnableTime, setNextEnableTime] = useState();
  const [requestSent, setRequestSent] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [username, setUsername] = useState();
  const { AuthStore, MemberStore } = useRootStore();
  const requestCode = useCallback(
    async email => {
      analytics.track(ANALYTICS_EVENTS.USER_PASSWORD_RESET_SUBMITTED);

      try {
        await AuthStore.forgotPassword(email);
        setRequestSent(true);
        setNextEnableTime(dayjs().add('1', 'minute'));
        setError(null);

        analytics.track(ANALYTICS_EVENTS.USER_PASSWORD_RESET);
      } catch (err) {
        if (err.code === 'UserNotFoundException') {
          try {
            await AuthStore.forgotPasswordWithMigration(email);
            setRequestSent(true);
            setNextEnableTime(dayjs().add('1', 'minute'));
          } catch (anotherError) {
            setError(anotherError.message);
          }
        } else {
          setError(err.message);
        }

        analytics.track(ANALYTICS_EVENTS.USER_PASSWORD_RESET_FAILED, {
          failureReason: err.message,
        });
      } finally {
        setUsername(email);
      }
    },
    [AuthStore]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      if (!nextEnableTime) {
        clearTimeout(timer);
        return;
      }
      const now = dayjs();
      if (now.isBefore(nextEnableTime)) {
        const seconds = Math.floor(dayjs.duration(nextEnableTime.diff(now)).asSeconds());
        if (seconds >= 0) {
          setTimeLeft(seconds);
        } else {
          setTimeLeft(0);
        }
      } else {
        clearTimeout(timer);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [nextEnableTime]);

  useEffect(() => {
    const disposer = autorun(() => {
      const { error: err } = MemberStore;
      if (err) {
        setError(err);
      }
    });
    return () => disposer();
  }, [AuthStore, MemberStore]);

  if (mfa.user) {
    return <MFAForm mfa={mfa} />;
  }

  if (requestSent) {
    return (
      <ResetCodeForm
        error={error}
        username={username}
        handleOnSubmit={async ({ code, newPassword, confirmPassword, mobile }) => {
          if (newPassword !== confirmPassword) {
            setError('Passwords don’t match');
          } else {
            try {
              await AuthStore.forgotPasswordSubmit({ username, code, newPassword, mobile });
              const { user } = await AuthStore.signIn({ email: username, password: newPassword });
              if (user.challengeName === 'SMS_MFA') {
                setError(null);
                setMfa({ user, email: username, password: newPassword });
              } else {
                setError(null);
              }

              // Successfully signed in
              analytics.reset(ANALYTICS_EVENTS.USER_PASSWORD_RESET);
            } catch (err) {
              setError(err.message);
            }
          }
        }}
        handleResendCode={() => {
          if (timeLeft === 0) {
            requestCode(username);
          } else {
            setRequestSent(false);
          }
        }}
      />
    );
  }

  return (
    <Form
      heading="Forgot Password"
      initialValues={{ email: '' }}
      onSubmit={({ email }) => requestCode(email)}
      validationSchema={schemas.forgotPassword}
      disabled={timeLeft > 0}
      buttonText="Send reset code"
      paragraph="A reset password code will be sent to your nominated email account."
    >
      <>
        <Box mb={1}>
          <FormikTextInput name="email" label="Email" type={INPUT_TYPE.EMAIL} autoComplete={INPUT_AUTOCOMPLETE.EMAIL} />
        </Box>
        {timeLeft > 0 && (
          <Box mb={1}>
            <Alert type="info">You can resend the code in {timeLeft} seconds</Alert>
          </Box>
        )}
      </>
    </Form>
  );
};

export { ForgotPasswordForm };
